/**
 * 安全なリダイレクトユーティリティ
 *
 * オープンリダイレクト脆弱性対策として、
 * ホワイトリストに含まれるURLのみリダイレクトを許可します
 */

/**
 * 許可されたリダイレクト先のホワイトリスト
 */
const ALLOWED_REDIRECT_URLS = [
  // アプリケーション内部パス
  '/admin/',
  '/useradmin/',
  '/kix/',
  '/kix-gbc/',

  // 外部サイト（完全一致）
  'https://www.kansai-airport.or.jp/kix-gbc/',
  'https://www.kansai-airport.or.jp/kix-gbc/faq/2.html',
  'https://www.kansai-airport.or.jp/kix-gbc/notices/200313/',
] as const;

/**
 * 許可されたドメインのホワイトリスト
 * このドメイン配下のすべてのパスを許可します
 */
const ALLOWED_DOMAINS = [
  'www.kansai-airport.or.jp',
] as const;

/**
 * URLが安全かどうかを検証する
 *
 * @param url - 検証するURL
 * @returns URLが安全な場合true、そうでない場合false
 */
export function isUrlSafe(url: string): boolean {
  try {
    // 相対パスの場合
    if (url.startsWith('/')) {
      // 許可されたパスプレフィックスで始まるかチェック
      return ALLOWED_REDIRECT_URLS.some(allowedUrl =>
        allowedUrl.startsWith('/') && url.startsWith(allowedUrl)
      );
    }

    // ハッシュルーティング（SPA内部）
    if (url.startsWith('#')) {
      return true;
    }

    // 完全一致チェック
    if (ALLOWED_REDIRECT_URLS.includes(url as any)) {
      return true;
    }

    // 絶対URLの場合
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlObj = new URL(url);

      // 許可されたドメインかチェック
      const isAllowedDomain = ALLOWED_DOMAINS.some(domain => {
        // 完全一致または、許可ドメインのサブドメインかチェック
        return urlObj.hostname === domain ||
               urlObj.hostname.endsWith('.' + domain);
      });

      if (isAllowedDomain) {
        return true;
      }

      // ドメイン + パスの完全一致チェック
      // 「https://example.com.attacker.com」のような迂回を防ぐ
      return ALLOWED_REDIRECT_URLS.some(allowedUrl => {
        if (!allowedUrl.startsWith('http')) return false;

        try {
          const allowedUrlObj = new URL(allowedUrl);

          // ホスト名が完全に一致し、パスが許可されたパスで始まる場合のみ許可
          return urlObj.hostname === allowedUrlObj.hostname &&
                 urlObj.pathname.startsWith(allowedUrlObj.pathname);
        } catch {
          return false;
        }
      });
    }

    return false;
  } catch (error) {
    // URL解析エラーの場合は安全でないと判断
    console.error('URL validation error:', error);
    return false;
  }
}

/**
 * 安全なリダイレクトを実行する
 *
 * @param url - リダイレクト先URL
 * @param fallbackUrl - URLが安全でない場合のフォールバックURL（デフォルト: '/'）
 * @throws {Error} URLが安全でない場合（productionではfallbackにリダイレクト）
 */
export function safeRedirect(url: string, fallbackUrl: string = '/'): void {
  if (isUrlSafe(url)) {
    window.location.href = url;
  } else {
    console.error(`Unsafe redirect blocked: ${url}`);

    // 開発環境ではエラーをスロー
    if (process.env.NODE_ENV === 'development') {
      throw new Error(`Unsafe redirect blocked: ${url}`);
    }

    // 本番環境ではフォールバックURLにリダイレクト
    window.location.href = fallbackUrl;
  }
}

/**
 * 安全なlocation.hrefの代替
 *
 * @param url - 設定するURL
 * @param fallbackUrl - URLが安全でない場合のフォールバックURL
 */
export function safeLocationHref(url: string, fallbackUrl: string = '/'): void {
  safeRedirect(url, fallbackUrl);
}
